import React from 'react';
import Transition from 'react-transition-group/Transition';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
} from 'web-selectors';

import {
  uiActions,
} from 'web-actions';

import {
  Flex,
} from 'web-styled';

import {
  getTourSteps,
  calculateOnboardPercentage,
} from './data';

import Tour from './Tour';
import Tables from './Tables';
import Indicators from './Indicators';
import Onboarding from './Onboarding';

const duration = 800;

const transitionStyles = {
  entering: { display: 'none' },
  entered: { display: 'block' },
};

const mapState = (state) => {
  const profile = coreSelectors.getProfile(state);
  return {
    profile,
    workoutEvents: coreSelectors.getEventsGroupByBeginsAt()(state),
    missedWorkouts: coreSelectors.getMissedWorkouts()(state),
    openedTooltips: getUiState('openedTooltips')(state),
    clientsPerformance: coreSelectors.getClientsPerformance(state),
  };
};

const Pro = () => {
  const {
    profile,
    workoutEvents,
    missedWorkouts,
    openedTooltips,
    clientsPerformance,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  const userSteps = profile.user_settings.tutorial_steps;
  const videoSeen = profile.user_settings.web_onboarding_video_seen;
  const percentage = calculateOnboardPercentage(userSteps);
  const tourSteps = getTourSteps(userSteps);

  const handleUpdateSteps = (steps: string[]) => {
    dispatch(
      coreActions.requestUpdateSettings({
        body: {
          tutorial_steps: [...new Set(steps)],
        },
        settingsId: profile.user_settings.id,
      }),
    );
  };

  const updateTooltips = (tips: string[]) => {
    dispatch(
      uiActions.setUiState({
        openedTooltips: [...new Set([...openedTooltips, ...tips])],
      }),
    );
  };

  const handleGoal = (current: string) => {
    switch (current) {
      case 'perform_workout':
        break;
      case 'review_documents':
        if (!openedTooltips.includes('Documents')) {
          updateTooltips(
            ['Documents', 'HTS Manual', 'view_document'],
          );
        }
        break;
      case 'got_questions':
        handleUpdateSteps([
          ...userSteps,
          'perform_workout',
          'review_documents',
          'got_questions',
        ]);
        break;
      default:
        break;
    }
  };

  return (
    <Flex
      column
      spacing={55}
      padding="41px 23px 54px"
      flex="1 1 0"
    >
      <Transition
        in={percentage === 100}
        timeout={duration}
        unmountOnExit
      >
        {state => (
          <div style={{
            width: '100%',
            ...transitionStyles[state],
          }}
          >
            <Indicators
              data={profile.dashboard || {}}
            />
          </div>
        )}
      </Transition>
      <Transition
        in={percentage !== 100}
        timeout={duration}
        unmountOnExit
      >
        {state => (
          <div style={{
            width: '100%',
            ...transitionStyles[state],
          }}
          >
            <Onboarding
              userSteps={userSteps}
              percentage={percentage}
              onGoal={handleGoal}
            />
          </div>
        )}
      </Transition>
      <Tables
        workoutEvents={workoutEvents}
        missedWorkouts={missedWorkouts}
        clientsPerformance={clientsPerformance}
      />
      {videoSeen && tourSteps.some(s => !s.isCompleted) && (
        <Tour
          tourSteps={tourSteps}
          handleOnPrev={(step) => {
            handleUpdateSteps(userSteps.filter(s => s !== step));
          }}
          handleOnNext={(step) => {
            handleUpdateSteps([...userSteps, step]);
          }}
        />
      )}
    </Flex>
  );
};

export default Pro;
